import React, { createContext, useContext, useEffect, useState } from 'react';
import api, { TOKEN_KEY, unwrap } from '../services/api';
import type { AuthUser } from '../types';
import { ensureAppAdmin } from '../lib/appAdminGate';

interface AuthState {
  user: AuthUser | null; token: string | null; isAuthenticated: boolean; loading: boolean;
  login: (email: string, password: string) => Promise<void>; logout: () => void;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState<boolean>(!!token);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    api.get('users/me')
      .then((r) => { const me = unwrap<AuthUser>(r); ensureAppAdmin(me); setUser(me); })
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setToken(null); })
      .finally(() => setLoading(false));
  }, [token]);

  const login = async (email: string, password: string) => {
    const res = await api.post('auth/login', { email, password });
    const data = unwrap<{ api_token: string; user: AuthUser }>(res);
    localStorage.setItem(TOKEN_KEY, data.api_token); // set so the api interceptor authorizes the users/me call
    try {
      const me = unwrap<AuthUser>(await api.get('users/me')); // full user incl. user_role
      ensureAppAdmin(me);
      setToken(data.api_token);
      setUser(me);
    } catch (e) {
      localStorage.removeItem(TOKEN_KEY); // rejected login leaves nothing stored
      setToken(null);
      throw e instanceof Error ? e : new Error('Login failed');
    }
  };

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(null); setUser(null); window.location.href = `${process.env.PUBLIC_URL ?? ''}/login`; };

  return <AuthContext.Provider value={{ user, token, isAuthenticated: !!token, loading, login, logout }}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
